import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_KEYS } from "./query-keys";
import { toast } from "sonner";

export const useExpenses = (userId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.expenses(userId),
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("expenses")
        .select(`
          *,
          payer:players!payer_id(name, nickname),
          participants:expense_participants(
            *,
            player:players(name, nickname)
          ),
          session:sessions(date)
        `)
        .eq("created_by", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });
};

const EXPENSE_SELECT = `
  *,
  payer:players!payer_id(name, nickname),
  participants:expense_participants(
    *,
    player:players(name, nickname)
  ),
  session:sessions(date)
`;

export const usePlayerExpenses = (playerId?: string) => {
  return useQuery({
    queryKey: ['expenses', 'player', playerId],
    staleTime: 30_000,
    queryFn: async () => {
      if (!playerId) return [];

      // Two parallel queries: expenses paid by player + expenses where player is participant
      const [paidResult, participantResult] = await Promise.all([
        supabase
          .from("expenses")
          .select(EXPENSE_SELECT)
          .eq("payer_id", playerId)
          .order("created_at", { ascending: false }),
        supabase
          .from("expense_participants")
          .select("expense_id")
          .eq("player_id", playerId),
      ]);

      if (paidResult.error) throw paidResult.error;

      const paidExpenses = paidResult.data || [];
      const paidIds = new Set(paidExpenses.map(e => e.id));

      // Fetch shared expenses (where player is participant but not payer)
      const sharedIds = (participantResult.data || [])
        .map(p => p.expense_id)
        .filter((eid): eid is string => !!eid && !paidIds.has(eid));

      if (sharedIds.length === 0) return paidExpenses;

      const { data: sharedData, error: sharedError } = await supabase
        .from("expenses")
        .select(EXPENSE_SELECT)
        .in("id", sharedIds)
        .order("created_at", { ascending: false });

      if (sharedError) throw sharedError;

      return [...paidExpenses, ...(sharedData || [])];
    },
    enabled: !!playerId,
  });
};

export const useCreateExpense = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ expense, participants }: { expense: any; participants: any[] }) => {
      const { data: expenseData, error: expenseError } = await supabase
        .from("expenses")
        .insert([expense])
        .select()
        .single();

      if (expenseError) throw expenseError;

      const participantInserts = participants.map((p) => ({
        ...p,
        expense_id: expenseData.id,
      }));

      const { error: participantsError } = await supabase
        .from("expense_participants")
        .insert(participantInserts);

      if (participantsError) throw participantsError;

      return expenseData;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses(data.created_by) });
      toast.success("Expense created successfully");
    },
    onError: (error: any) => {
      toast.error(`Error creating expense: ${error.message}`);
    },
  });
};

export const useDeleteExpense = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const { error } = await supabase.from("expenses").delete().eq("id", id);
      if (error) throw error;
      return { id, userId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses(data.userId) });
      toast.success("Expense deleted successfully");
    },
    onError: (error: any) => {
      toast.error(`Error deleting expense: ${error.message}`);
    },
  });
};

export const useUpdateExpense = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expense, participants }: { id: string; expense: any; participants: any[] }) => {
      const { data: expenseData, error: expenseError } = await supabase
        .from("expenses")
        .update(expense)
        .eq("id", id)
        .select()
        .single();

      if (expenseError) throw expenseError;

      // Delete existing participants
      const { error: deleteError } = await supabase
        .from("expense_participants")
        .delete()
        .eq("expense_id", id);

      if (deleteError) throw deleteError;

      // Insert new participants
      const participantInserts = participants.map((p) => ({
        ...p,
        expense_id: id,
      }));

      const { error: participantsError } = await supabase
        .from("expense_participants")
        .insert(participantInserts);

      if (participantsError) throw participantsError;

      return expenseData;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses(data.created_by) });
      toast.success("Expense updated successfully");
    },
    onError: (error: any) => {
      toast.error(`Error updating expense: ${error.message}`);
    },
  });
};
